import "../../pages/login/login.styles.scss";

import QRCode from "qrcode.react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Icon from "../../components/Icon/Icon";
import InputText from "../../components/InputText/InputText";
import { verify2FACode } from "../../redux/user/user.action";
import { getUserGoogleSecretKey, sendTwoFAPhoneNumber } from "../../services/twofactor.service";

function TwoFactorForm() {
	const navigate = useNavigate();

	const user = useSelector((state) => state.user);
	const themeColor = useSelector((state) => state.theme);
	const [key, setKey] = useState("Fetching key...");
	const [step, setStep] = useState(1);
	const dispatch = useDispatch();
	const [twoFACode, setTwoFACode] = useState("");
	const [twoFactorSelectedOption, setTwoFactorSelectedOption] = useState("");
	const [smsConsent, setSMSConsent] = useState(false);
	const [smsPhoneNumber, setSMSPhoneNumber] = useState();

	const handleSubmit = async () => {
		dispatch(verify2FACode(twoFACode, navigate, twoFactorSelectedOption, smsPhoneNumber));
	};

	const runFetch = async () => {
		const res = await getUserGoogleSecretKey();
		if (res.code === 200) {
			setKey(res.data.key);
		}
	};

	useEffect(() => {
		if (!user.user_confirmed_2fa) {
			runFetch();
		}
	}, [user]);

	const copyToClipboard = (codeToCopy) => {
		if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
			return navigator.clipboard
				.writeText(codeToCopy)
				.then(() => {
					alert("2FA Secret Key copied to clipboard.");
				})
				.catch((error) => {
					console.error("Failed to copy text to clipboard:", error);
				});
		}

		return Promise.reject("The Clipboard API is not available.");
	};

	const googleAuthenticatorUri = `otpauth://totp/Bluechip%20Insights:${user.email}?secret=${key}&issuer=Bluechip%20Insights`;

	const formatPhoneNumber = (value) => {
		// Remove all non-numeric characters
		const digits = value.replace(/\D/g, "");

		// Format as (XXX-XXX-XXXX)
		if (digits.length <= 3) {
			return digits;
		} else if (digits.length <= 6) {
			return `${digits.slice(0, 3)}-${digits.slice(3)}`;
		} else {
			return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
		}
	};

	const handlePhoneNumberChange = (e) => {
		const formattedNumber = formatPhoneNumber(e);
		setSMSPhoneNumber(formattedNumber);
	};

	useEffect(() => {
		document.body.className = themeColor;

		if (user.user_confirmed_2fa && user.two_factor_option === "sms" && user.phone !== "") {
			setTwoFactorSelectedOption("sms");
			setSMSPhoneNumber(user.phone);
			sendTwoFAPhoneNumber(user.phone);
		}
	}, []);

	useEffect(() => {
		if (step === 2 && twoFactorSelectedOption === "sms" && smsPhoneNumber !== "") {
			sendTwoFAPhoneNumber(smsPhoneNumber);
		}
	}, [step]);

	const content = () => {
		return (
			<>
				{user.user_confirmed_2fa ? (
					<>
						<h4 style={{ fontWeight: "bold" }}>2 Factor Authentication</h4>
						{user.two_factor_option === "sms" ? (
							<>
								<p>Please enter the code you received from the SMS message.</p>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										handleSubmit();
									}}
								>
									<InputText
										srOnlyLabel={"Authenticator Code"}
										placeholder={"e.g. 123456"}
										value={twoFACode}
										onValueChange={(e) => setTwoFACode(e)}
									/>

									<input className='btn primary rounded' type='submit' value='Submit' />
								</form>
							</>
						) : (
							<>
								<p>Please enter the code from your authenticator app in the field below</p>
								<form
									onSubmit={(e) => {
										e.preventDefault();
										handleSubmit();
									}}
								>
									<InputText
										srOnlyLabel={"Authenticator Code"}
										placeholder={"e.g. 123456"}
										value={twoFACode}
										onValueChange={(e) => setTwoFACode(e)}
									/>

									<input className='btn primary rounded' type='submit' value='Submit' />
								</form>
							</>
						)}
					</>
				) : (
					<>
						{step === 1 ? (
							<>
								<h4 style={{ fontWeight: "bold" }}>2 Factor Authentication</h4>
								<p>2 Factor Authentication is required to login.</p>
								<div id='two-factor-authentication-options'>
									<h5>Pick A Two Factor Option</h5>
									<ul className=''>
										<li
											onClick={() => setTwoFactorSelectedOption("sms")}
											className={`${twoFactorSelectedOption === "sms" ? "active" : "inactive"}`}
										>
											<h1>SMS</h1>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 24 24'
												fill='currentColor'
											>
												<path d='M6.45455 19L2 22.5V4C2 3.44772 2.44772 3 3 3H21C21.5523 3 22 3.44772 22 4V18C22 18.5523 21.5523 19 21 19H6.45455ZM5.76282 17H20V5H4V18.3851L5.76282 17ZM11 10H13V12H11V10ZM7 10H9V12H7V10ZM15 10H17V12H15V10Z'></path>
											</svg>
										</li>
										<li
											onClick={() => setTwoFactorSelectedOption("authenticator")}
											className={`${
												twoFactorSelectedOption === "authenticator" ? "active" : "inactive"
											}`}
										>
											<h1>Authentication App</h1>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												viewBox='0 0 24 24'
												fill='currentColor'
											>
												<path d='M7 4V20H17V4H7ZM6 2H18C18.5523 2 19 2.44772 19 3V21C19 21.5523 18.5523 22 18 22H6C5.44772 22 5 21.5523 5 21V3C5 2.44772 5.44772 2 6 2ZM12 17C12.5523 17 13 17.4477 13 18C13 18.5523 12.5523 19 12 19C11.4477 19 11 18.5523 11 18C11 17.4477 11.4477 17 12 17Z'></path>
											</svg>
										</li>
									</ul>
								</div>

								{twoFactorSelectedOption === "authenticator" && (
									<>
										<QRCode value={googleAuthenticatorUri} />
										<p>Do not have an Authenticator App?</p>
										<ul aria-label='Google Authenticator'>
											<li>
												<a href='https://apps.apple.com/us/app/google-authenticator/id388497605'>
													Apple
												</a>
											</li>
											<li>
												<a href='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US&gl=US'>
													Google
												</a>
											</li>
										</ul>
										<p>
											<ul aria-label='Microsoft Authenticator'>
												<li>
													<a href='https://apps.apple.com/us/app/microsoft-authenticator/id983156458'>
														Apple
													</a>
												</li>
												<li>
													<a href='https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en_US&gl=US'>
														Google
													</a>
												</li>
											</ul>
										</p>
										<p>
											<details className='alternatives'>
												<summary>Click here for additional options</summary>
												<li>
													<a
														target='_blank'
														rel='noreferrer'
														href='https://chrome.google.com/webstore/detail/authenticator/bhghoamapcdpbohphigoooaddinpkbai'
													>
														Authenticator for Chrome
													</a>
												</li>
												<li>
													<a
														target='_blank'
														rel='noreferrer'
														href='https://addons.mozilla.org/en-US/firefox/addon/auth-helper/'
													>
														Authenticator for Firefox
													</a>
												</li>
												<li>
													<a
														target='_blank'
														rel='noreferrer'
														href='https://microsoftedge.microsoft.com/addons/detail/authenticator-2fa-client/ocglkepbibnalbgmbachknglpdipeoio'
													>
														Authenticator for Edge
													</a>
												</li>

												<p>
													Please use this secret key with an authenticator application for
													generating <br></br>time-based one-time passwords.
												</p>
												<p
													style={{ cursor: "pointer" }}
													onClick={() => {
														copyToClipboard(key);
													}}
												>
													<b>
														Secret Key: {key}{" "}
														<Icon icon={"file-copy-line"} viewBox={"0 0 24 24"} />
													</b>
												</p>
											</details>
										</p>
									</>
								)}
								{twoFactorSelectedOption === "sms" && (
									<div id='sms-authentication'>
										<InputText
											label={"Phone Number"}
											placeholder={"e.g. 555-555-5555"}
											value={smsPhoneNumber}
											onValueChange={(e) => handlePhoneNumberChange(e)}
										/>
										<div className='verification-checkbox'>
											<input
												type='checkbox'
												name='verify'
												value={smsConsent}
												onChange={() => setSMSConsent(!smsConsent)}
											/>
											<p>
												Please check the box to consent to receiving text messages from
												Bluechip.
											</p>
										</div>
									</div>
								)}
							</>
						) : (
							<>
								{twoFactorSelectedOption === "authenticator" && (
									<InputText
										label={"Authenticator Code"}
										placeholder={"e.g. 123456"}
										value={twoFACode}
										onValueChange={(e) => setTwoFACode(e)}
									/>
								)}
								{twoFactorSelectedOption === "sms" && (
									<div style={{ width: "75%", margin: "0 auto" }}>
										<h3>Two-Factor Code</h3>
										<p>Please enter in the code you received.</p>
										<InputText
											label={"SMS Code"}
											placeholder={"e.g. 123456"}
											value={twoFACode}
											onValueChange={(e) => setTwoFACode(e)}
										/>
									</div>
								)}
							</>
						)}
						<p>
							{step === 2 && twoFactorSelectedOption === "authenticator" && (
								<button
									onClick={() => {
										if (key === twoFACode) {
											alert(
												"You hav entered the Google Secret Key. Please add the Secret Key to your authenticator app and enter the provided code.",
											);
										} else {
											handleSubmit();
										}
									}}
									style={{ marginRight: "10px" }}
									className='btn primary rounded'
								>
									Verify 2FA Code
								</button>
							)}
							{step === 2 && twoFactorSelectedOption === "sms" && (
								<button
									onClick={() => handleSubmit()}
									style={{ marginRight: "10px" }}
									className='btn primary rounded'
								>
									Verify 2FA Code
								</button>
							)}

							{step === 1 && twoFactorSelectedOption !== "" && (
								<button
									className='btn outline rounded'
									onClick={() => {
										step === 1 ? setStep(2) : setStep(1);
									}}
								>
									Confirm Selected Option
								</button>
							)}
						</p>
					</>
				)}
			</>
		);
	};

	return (
		<>
			<section className='login-sec'>
				<div className='bg-pattern'>
					<img src='../assets/images/login-pattern.svg' alt='' />
				</div>
				<div className='container'>
					<div className='row'>
						<div className='col col-12 mx-auto'>
							<div className='login-card-wrapper card' style={{ width: "100%", maxWidth: "700px" }}>
								<div className='card-body text-center'>
									<div className='site-logo-icon'>
										<img
											data-theme={themeColor.theme_color}
											src={
												themeColor.theme_color !== "dark-theme"
													? import.meta.env.VITE_LIGHT_LOGO_PATH
													: import.meta.env.VITE_DARK_LOGO_PATH
											}
											alt=''
										/>
									</div>
									{content()}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}

export default TwoFactorForm;
